"use client"

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cva, type VariantProps } from "class-variance-authority"
import { CircleIcon } from "lucide-react"
import type * as React from "react"

import { cn } from "../lib/utils"

const radioGroupItemVariants = cva(
	[
		"cursor-pointer",
		"hover:brightness-90 dark:hover:brightness-75",
		"text-[var(--radio-group-item-foreground)] bg-[var(--radio-group-item-background)] dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border outline-none",
		"disabled:cursor-not-allowed disabled:opacity-50",
		"data-[state=unchecked]:border-input",
		"data-[state=checked]:border-[var(--radio-group-item-complement)]",
		"focus-visible:ring-[var(--radio-group-item-foreground)] focus-visible:ring-[3px]",
		"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
	],
	{
		variants: {
			palette: {
				default: [
					"[--radio-group-item-background:var(--color-accent)]",
					"[--radio-group-item-foreground:var(--color-foreground)]",
					"[--radio-group-item-complement:var(--color-foreground)]"
				],
				betta: [
					"[--radio-group-item-background:var(--color-accent)]",
					"[--radio-group-item-foreground:var(--color-betta)]",
					"[--radio-group-item-complement:var(--color-butterfly)]"
				],
				cardinal: [
					"[--radio-group-item-background:var(--color-accent)]",
					"[--radio-group-item-foreground:var(--color-cardinal)]",
					"[--radio-group-item-complement:var(--color-fire-ant)]"
				],
				bee: [
					"[--radio-group-item-background:var(--color-accent)]",
					"[--radio-group-item-foreground:var(--color-bee)]",
					"[--radio-group-item-complement:var(--color-lion)]"
				],
				owl: [
					"[--radio-group-item-background:var(--color-accent)]",
					"[--radio-group-item-foreground:var(--color-owl)]",
					"[--radio-group-item-complement:var(--color-tree-frog)]"
				],
				macaw: [
					"[--radio-group-item-background:var(--color-accent)]",
					"[--radio-group-item-foreground:var(--color-macaw)]",
					"[--radio-group-item-complement:var(--color-whale)]"
				]
			}
		},
		defaultVariants: {
			palette: "default"
		}
	}
)

function RadioGroup({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
	return <RadioGroupPrimitive.Root data-slot="radio-group" className={cn("grid gap-3", className)} {...props} />
}

function RadioGroupItem({
	className,
	palette = "default",
	...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item> & VariantProps<typeof radioGroupItemVariants>) {
	return (
		<RadioGroupPrimitive.Item
			data-slot="radio-group-item"
			data-palette={palette}
			className={cn(radioGroupItemVariants({ palette, className }))}
			{...props}
		>
			<RadioGroupPrimitive.Indicator
				data-slot="radio-group-indicator"
				className="relative flex items-center justify-center"
			>
				<CircleIcon className="fill-[var(--radio-group-item-foreground)] absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
			</RadioGroupPrimitive.Indicator>
		</RadioGroupPrimitive.Item>
	)
}

export { RadioGroup, RadioGroupItem, radioGroupItemVariants }
